import { QESButton } from "@app/shared/qes-standards/Button";
import { ToggleButtonSharedProps } from "@app/shared/toggle-button/toggleButtonSharedProps";
import { FC, FocusEvent, KeyboardEvent, useMemo, useRef } from "react";

export interface ToggleButtonProps extends ToggleButtonSharedProps {
  /**
   * A description of what's being selected, used for accessibility metadata.
   */
  groupLabel: string;
  /**
   * The selected button's key
   */
  selectedButton: string;
}

/**
 * A list of buttons that work like a group of radio buttons. This supports any number of buttons (but if you have more than about 3-5 you probably want a Select instead).
 * <br />This component is dumb and stateless. The parent component will have to define onClick effects and maintain selections.
 * <br />This component implements the radio group pattern from WCAG for accessibility.
 */
export const ToggleButton: FC<ToggleButtonProps> = (props: ToggleButtonProps) => {
  const size = props.size ?? "medium";
  const selectedButtonType = props.selectedButtonType ?? "primary";
  const groupRef = useRef<HTMLDivElement>(null);
  const unselectedButtonType = useMemo(() => {
    switch (selectedButtonType) {
      case "outline":
        return "outline";
      case "primary":
        return "secondary";
    }
  }, [selectedButtonType]);

  const handleGroupFocus = (e: FocusEvent<HTMLDivElement>) => {
    // the focus event bubbles, so we have to exclude descendents or this will override handleGroupKeyUp
    if (groupRef.current.contains(e.relatedTarget as Node) || props.disabled) {
      return;
    }
    // if we don't remove the element from the tab order, shift+tab focuses the radioGroup and triggers this function again
    groupRef.current.tabIndex = -1;
    const buttons: HTMLElement[] = Array.from(groupRef.current.querySelectorAll(".qes-button"));
    const activeButton = buttons.find((b) => b.classList.contains("active"));
    if (!!activeButton) {
      (activeButton).focus();
    } else {
      (buttons[0]).focus();
    }
  }

  const handleGroupBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (groupRef.current.contains(e.relatedTarget as Node) || props.disabled) {
      return;
    }
    groupRef.current.tabIndex = 0;
  }

  const handleGroupKeyUp = (e: KeyboardEvent) => {
    if (props.disabled) {
      return;
    }
    const buttons: HTMLElement[] = Array.from(groupRef.current.querySelectorAll(".qes-button"));
    const focusedButtonIndex = buttons.findIndex((b) => b === document.activeElement);
    let toFocus: HTMLElement;

    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        // next item + wrap around from last
        if (focusedButtonIndex === buttons.length - 1) {
          toFocus = buttons[0];
        } else {
          toFocus = buttons[focusedButtonIndex + 1];
        }
        toFocus.focus();
        props.onClick(toFocus.getAttribute("data-key"));
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        // previous item + wrap around from first
        if (focusedButtonIndex === 0) {
          toFocus = buttons[buttons.length - 1];
        } else {
          toFocus = buttons[focusedButtonIndex - 1];
        }
        toFocus.focus();
        props.onClick(toFocus.getAttribute("data-key"));
        break;
    }
  }

  return (
    <div
      role="radiogroup"
      className="toggle-button__group"
      tabIndex={props.disabled ? -1 : 0}
      ref={groupRef}
      onKeyDown={handleGroupKeyUp}
      onFocus={handleGroupFocus}
      onBlur={handleGroupBlur}
      aria-label={props.groupLabel}
    >
      <ul className={`toggle-button__list toggle-button__list--${size}`}>
        {Object.entries(props.buttons).map(([key, label]) => {
          const isActive = props.selectedButton === key;
          return (
            <li key={key}>
              <QESButton
                qesTestId={`${props.testId}__toggle-button--${key}`}
                qesType={isActive ? selectedButtonType : unselectedButtonType}
                className={isActive ? "active" : undefined}
                onClick={() => props.onClick(key)}
                disabled={props.disabled}
                role={"radio"}
                aria-checked={isActive}
                tabIndex={-1}
                data-key={key}
              >
                {label}
              </QESButton>
            </li>
          );
        })}
      </ul>
    </div>
  )
}
